import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { ProjectsService } from '../../../../../../core/projects.service';
import { RiskRegisterService } from '../risk-register.service';
import { Risk, RiskTrend, RiskUpdate } from '../risk-register.models';
import { RISK_ATTACHMENT_ACCEPT, RISK_ATTACHMENT_TAG, RiskMediaFile, scoreOf, serverErrorMessage, toRiskMedia, trendOf } from '../risk-register.util';

const LEVEL = [Validators.min(1), Validators.max(5)];

/** "Add update": optional new probability/impact (Figma Selector, 1–5), required comment, attachments. */
@Component({
  selector: 'app-risk-update-form',
  templateUrl: './risk-update-form.component.html',
  styleUrls: ['./risk-update-form.component.scss'],
  standalone: false,
})
export class RiskUpdateFormComponent implements OnInit, OnDestroy {
  @Input() risk: Risk;
  @Output() saved = new EventEmitter<RiskUpdate>();
  @Output() cancelled = new EventEmitter<void>();

  form = new FormGroup({
    probability: new FormControl<number>(null, LEVEL),
    impact: new FormControl<number>(null, LEVEL),
    comment: new FormControl('', [Validators.required, Validators.pattern(/\S/), Validators.maxLength(4000)]),
  });
  levels = [1, 2, 3, 4, 5];
  files: RiskMediaFile[] = [];
  readonly attachmentTag = RISK_ATTACHMENT_TAG;
  readonly attachmentAccept = RISK_ATTACHMENT_ACCEPT;
  saving = false;
  private readonly onDestroy = new Subject<void>();

  private riskService = inject(RiskRegisterService);
  private commonService = inject(CommonService);
  private projectsService = inject(ProjectsService);

  ngOnInit(): void {
    this.form.patchValue({ probability: this.risk.probability, impact: this.risk.impact });
    this.commonService.uploadedFilesInfo.pipe(takeUntil(this.onDestroy)).subscribe(data => {
      const items: RiskMediaFile[] = JSON.parse(JSON.stringify(Array.isArray(data) ? data : [data]));
      this.setFiles([...this.files, ...items.filter(f => f?.id).map(toRiskMedia)]);
    });
  }

  get newScore(): number { return scoreOf(this.form.value.probability, this.form.value.impact); }
  get changed(): boolean {
    return this.form.value.probability !== this.risk.probability || this.form.value.impact !== this.risk.impact;
  }
  get trend(): RiskTrend { return trendOf(this.risk.score, this.newScore); }
  pick(field: 'probability' | 'impact', v: number): void { this.form.get(field).setValue(v); this.form.markAsDirty(); }

  removeFile(f: RiskMediaFile): void { this.setFiles(this.files.filter(x => x.id !== f.id)); }

  /** app-pictures-section-with-tags renders from ProjectsService.mediaData, not its input (punchlist pattern). */
  private setFiles(files: RiskMediaFile[]): void {
    this.files = files;
    setTimeout(() => this.projectsService.mediaData.next(this.files));
  }

  save(): void {
    if (this.form.invalid || this.saving) return;
    this.saving = true;
    const v = this.form.value;
    this.riskService.addUpdate(this.risk.id, {
      comment: v.comment.trim(),
      probability: v.probability !== this.risk.probability ? v.probability : undefined,
      impact: v.impact !== this.risk.impact ? v.impact : undefined,
      attachments: this.files.map(f => f.id),
    }).pipe(takeUntil(this.onDestroy)).subscribe({
      next: update => { this.saving = false; this.commonService.notification('Update saved', 'success'); this.saved.emit(update); },
      error: err => { this.saving = false; this.commonService.notification(serverErrorMessage(err?.error, 'Could not save the update'), 'error'); },
    });
  }

  ngOnDestroy(): void { this.onDestroy.next(); this.onDestroy.complete(); }
}
